import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ShieldAlert, Satellite, CalendarClock, ClipboardCheck, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { KpiCard, PageHeader, Panel } from "@/components/common";
import { ASTATUS, Chip, LEVEL, SAT } from "@/components/risk";

const bars = (obj, map) => Object.entries(obj).map(([k, v]) => ({ name: map[k]?.[0]?.split(" —")[0] || k, value: v, color: map[k]?.[2] || "#94A3B8" }));

function ChartBox({ title, data, testid, pie }) {
  return (
    <Panel title={title} testid={testid}>
      <div className="h-56">
        <ResponsiveContainer initialDimension={{ width: 400, height: 220 }}>
          {pie ? (
            <PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>{data.map((d) => <Cell key={d.name} fill={d.color} />)}</Pie><Tooltip /></PieChart>
          ) : (
            <BarChart data={data}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>{data.map((d) => <Cell key={d.name} fill={d.color} />)}</Bar></BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {pie && <div className="mt-2 flex flex-wrap gap-3 text-xs">{data.map((d) => <span key={d.name} className="flex items-center gap-1"><i className="h-2 w-2 rounded-full" style={{ background: d.color }} />{d.name} {d.value}</span>)}</div>}
    </Panel>
  );
}

export default function RiskDashboard() {
  const { params } = usePlant();
  const { can } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/risk/dashboard", { params }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const runChecks = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/risk/run-daily-checks")).data;
      toast.success(`Daily checks done: ${r.expired} expired certificate(s), ${r.warned_30d} 30-day warning(s), ${r.yearly_reviews} yearly review(s); ${r.satellite_retry.pending_plots} pending satellite check(s)${r.satellite_retry.retried ? " retried" : " (GFW not connected)"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return <div className="py-24 text-center text-sm text-slate-400">Loading…</div>;
  const warn = d.expiries.filter((e) => e.state !== "upcoming").length;
  return (
    <div data-testid="risk-dashboard-page">
      <PageHeader title="Risk dashboard" subtitle="EUDR risk assessments across suppliers: rules-based score, EU country benchmark, Global Forest Watch satellite checks and sign-off status.">
        {can.admin && <Button variant="outline" data-testid="run-daily-checks-btn" disabled={busy} onClick={runChecks}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}Run daily checks now</Button>}
      </PageHeader>
      {!d.gfw.connected && <p className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="gfw-not-connected-banner"><AlertTriangle className="h-4 w-4" />Global Forest Watch is not connected – satellite checks stay “Pending — satellite check unavailable”. An admin can connect it in Settings → Global Forest Watch.</p>}
      {d.gfw.expiring_soon && <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="gfw-expiry-banner">GFW API key expires {fmtDate(d.gfw.expires_at)} – reconnect soon.</p>}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Assessed suppliers" value={`${d.assessed} / ${d.suppliers}`} icon={ClipboardCheck} testid="kpi-assessed" />
        <KpiCard label="High risk" value={d.by_level.high} tone="rose" icon={ShieldAlert} testid="kpi-high" />
        <KpiCard label="Satellite pending" value={d.satellite.pending} tone="amber" icon={Satellite} testid="kpi-sat-pending" />
        <KpiCard label="Expiries & reviews" value={warn + d.reviews.length} tone="brand" icon={CalendarClock} testid="kpi-expiries" />
      </div>
      <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-4">
        <ChartBox title="Suppliers by risk level" data={bars(d.by_level, LEVEL)} testid="chart-risk-level" pie />
        <ChartBox title="By EU country benchmark" data={bars(d.by_benchmark, LEVEL)} testid="chart-benchmark" />
        <ChartBox title="Satellite results (plots)" data={bars(d.satellite, SAT)} testid="chart-satellite" />
        <ChartBox title="Assessments by status" data={bars(d.by_status, ASTATUS)} testid="chart-status" />
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <Panel title="Upcoming certificate expiries" subtitle="Warning 30 days before expiry" testid="expiries-panel">
          {d.expiries.map((e, i) => (
            <button key={i} onClick={() => nav(`/suppliers/${e.supplier_id}?tab=risk`)} className="flex w-full items-center justify-between border-b border-slate-100 py-2 text-left text-sm hover:bg-slate-50" data-testid={`expiry-row-${i}`}>
              <span className="min-w-0 truncate"><b>{e.supplier}</b> · {e.filename}</span>
              <span className={e.state === "expired" ? "text-rose-600" : e.state === "warning" ? "text-amber-700" : "text-slate-500"}>{e.state === "expired" ? `expired ${e.expiry}` : `${e.days} days`}</span>
            </button>
          ))}
          {!d.expiries.length && <p className="text-sm text-slate-500">No certificates expire in the next 60 days.</p>}
        </Panel>
        <Panel title="Yearly reviews" subtitle="Due 12 months after approval" testid="reviews-panel">
          {d.reviews.map((r) => (
            <button key={r.supplier_id} onClick={() => nav(`/suppliers/${r.supplier_id}?tab=risk`)} className="flex w-full justify-between border-b border-slate-100 py-2 text-left text-sm hover:bg-slate-50">
              <b className="truncate">{r.supplier}</b><span className={r.overdue ? "text-rose-600" : "text-slate-500"}>{r.overdue ? "review overdue" : `due ${fmtDate(r.review_due_at)}`}</span>
            </button>
          ))}
          {!d.reviews.length && <p className="text-sm text-slate-500">No reviews due in the next 60 days.</p>}
        </Panel>
        <Panel title="Highest scores" testid="top-risk-panel">
          {d.top.map((t) => (
            <button key={t.supplier_id} onClick={() => nav(`/suppliers/${t.supplier_id}?tab=risk`)} className="flex w-full items-center justify-between gap-2 border-b border-slate-100 py-2 text-left text-sm hover:bg-slate-50">
              <span className="min-w-0 truncate"><span className="font-mono text-xs text-slate-500">{t.vendor}</span> {t.supplier}</span>
              <span className="flex items-center gap-2"><b>{t.score}</b><Chip map={LEVEL} value={t.level} /><Chip map={ASTATUS} value={t.status} /></span>
            </button>
          ))}
          {!d.top.length && <p className="text-sm text-slate-500">No assessments yet.</p>}
        </Panel>
      </div>
    </div>
  );
}
